import { useMemo, useState } from "react";
import styled from "styled-components";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Row,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import { ArrowClockwise, Graph } from "@phosphor-icons/react";

import { getMatterGraphInspector } from "../../../service/graphRuns";

const { Title, Paragraph, Text } = Typography;

const DEFAULT_MATTER_ID = "a0LU1000001hkqDMAQ";

const GraphInspector = () => {
  const [matterIdInput, setMatterIdInput] = useState(DEFAULT_MATTER_ID);
  const [matterId, setMatterId] = useState("");
  const [payload, setPayload] = useState(null);
  const [activeTable, setActiveTable] = useState("conditions");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadGraph = async (nextMatterId = matterIdInput.trim()) => {
    if (!nextMatterId) {
      setError("Enter a matter id.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await getMatterGraphInspector(nextMatterId);
      setPayload(response.data);
      setMatterId(nextMatterId);
      const firstTable = response.data?.tables?.[0]?.key;
      if (firstTable) setActiveTable(firstTable);
    } catch (err) {
      setPayload(null);
      setMatterId("");
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to load graph inspector data."
      );
    } finally {
      setLoading(false);
    }
  };

  const tableItems = useMemo(
    () =>
      (payload?.tables || []).map((table) => ({
        key: table.key,
        label: (
          <span>
            {table.label} <Tag>{table.rows?.length || 0}</Tag>
          </span>
        ),
        children: (
          <Table
            rowKey={(record) =>
              `${table.key}-${(table.columns || [])
                .map((column) => record[column] || "")
                .join("|")}`
            }
            dataSource={table.rows || []}
            columns={buildColumns(table.columns || [])}
            pagination={{ pageSize: 25, showSizeChanger: true }}
            scroll={{ x: "max-content" }}
            size="small"
            locale={{ emptyText: `No ${table.label.toLowerCase()} rows.` }}
          />
        ),
      })),
    [payload]
  );

  return (
    <Wrap>
      <TopRow>
        <div>
          <Title level={3}>Matter Graph Inspector</Title>
          <Paragraph type="secondary">
            Read-only Oxigraph tables for the matter graph. Use this to inspect
            what the pipeline extracted: conditions, decisions, evidence,
            medical facts, medications, labs, imaging, and employment.
          </Paragraph>
        </div>
      </TopRow>

      <Toolbar>
        <Input
          data-testid="graph-inspector-matter-input"
          value={matterIdInput}
          onChange={(event) => setMatterIdInput(event.target.value)}
          onPressEnter={() => loadGraph()}
          placeholder="Matter ID, e.g. a0LU1000001hkqDMAQ"
          style={{ maxWidth: 360 }}
        />
        <Button
          type="primary"
          icon={<Graph size={16} />}
          onClick={() => loadGraph()}
          loading={loading}
        >
          Load graph
        </Button>
        <Button
          icon={<ArrowClockwise size={16} />}
          onClick={() => loadGraph(matterId || matterIdInput.trim())}
          disabled={loading || (!matterId && !matterIdInput.trim())}
        >
          Refresh
        </Button>
      </Toolbar>

      {error ? <Alert type="error" showIcon message={error} /> : null}

      {loading ? (
        <LoadingWrap>
          <Spin size="large" />
        </LoadingWrap>
      ) : null}

      {!loading && !payload ? (
        <Empty description="Load a matter graph to inspect extracted rows." />
      ) : null}

      {payload ? (
        <>
          <SummaryCards>
            <Row gutter={[16, 16]}>
              {(payload.entity_counts || []).slice(0, 8).map((item) => (
                <Col xs={12} md={6} lg={3} key={item.type}>
                  <Card size="small">
                    <Statistic title={item.type} value={item.count || 0} />
                  </Card>
                </Col>
              ))}
            </Row>
            <Text type="secondary">
              Matter: <Mono>{payload.matter_id}</Mono>.{" "}
              {payload.row_limit
                ? `Showing up to ${payload.row_limit} rows per table.`
                : "Showing every row in the graph."}
            </Text>
          </SummaryCards>

          <Tabs
            activeKey={activeTable}
            onChange={setActiveTable}
            items={tableItems}
          />
        </>
      ) : null}
    </Wrap>
  );
};

function buildColumns(columns) {
  return columns.map((column) => ({
    title: formatColumn(column),
    dataIndex: column,
    key: column,
    width: column === "citation_snippet" ? 420 : 210,
    render: (value) => {
      if (!value) return <Muted>—</Muted>;
      if (column === "source_document_id") return <Mono>{value}</Mono>;
      return <CellText title={String(value)}>{String(value)}</CellText>;
    },
  }));
}

function formatColumn(column) {
  return String(column)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

// The Settings tab pane is a flex column with overflow hidden, so each tab
// must own its vertical scrolling (same pattern as CostAnalysis's Wrap).
const Wrap = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;
  padding-bottom: 48px;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
`;

const SummaryCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 16px 0 20px;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px;
`;

const CellText = styled.span`
  display: inline-block;
  max-width: 420px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Mono = styled.code`
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  font-size: 12px;
`;

const Muted = styled.span`
  color: #8c8c8c;
`;

export default GraphInspector;
